import { formatQuantity } from '../../utils/inventoryLabels';
import { EMPTY_PRODUCT_FORM, KIT_MODE } from './catalogConstants';

const valueOrEmpty = (value) => value ?? '';

/**
 * The form of a product, seeded from the one being edited.
 *
 * @param {object|null} product - As the backend answers it; `null` for a new one.
 */
export const productFormFrom = (product) =>
  product
    ? {
        sku: product.sku || '',
        name: product.name || '',
        barcode: product.barcode || '',
        price: valueOrEmpty(product.price),
        base_uom: product.base_uom || 'un',
        allows_fractional_qty: Boolean(product.allows_fractional_qty),
        lot_controlled: Boolean(product.lot_controlled),
        expiry_controlled: Boolean(product.expiry_controlled),
        serial_controlled: Boolean(product.serial_controlled),
        is_kit: Boolean(product.is_kit),
        kit_mode: product.kit_mode || KIT_MODE.VIRTUAL,
        components: (product.components || []).map((component) => ({
          productId: component.product,
          quantity: Number(component.quantity),
        })),
        rotation_class: product.rotation_class || 'B',
        weight: valueOrEmpty(product.weight),
        height: valueOrEmpty(product.height),
        width: valueOrEmpty(product.width),
        length: valueOrEmpty(product.length),
        is_active: product.is_active,
      }
    : { ...EMPTY_PRODUCT_FORM };

const numberOrNull = (value) => (value === '' || value === null ? null : Number(value));

/**
 * What the form sends.
 *
 * An emptied measure travels as `null` — unknown, not zero — except the weight,
 * which the model does not allow empty. A product that is not a kit sends no
 * components, so unticking the box does not leave a ghost list behind.
 */
export const buildProductPayload = (form) => ({
  sku: form.sku.trim(),
  name: form.name.trim(),
  barcode: form.barcode.trim(),
  price: numberOrNull(form.price),
  base_uom: form.base_uom.trim() || 'un',
  allows_fractional_qty: form.allows_fractional_qty,
  lot_controlled: form.lot_controlled,
  expiry_controlled: form.expiry_controlled,
  serial_controlled: form.serial_controlled,
  is_kit: form.is_kit,
  kit_mode: form.kit_mode,
  components: form.is_kit
    ? form.components
        .filter((component) => component.productId)
        .map((component) => ({
          product_id: component.productId,
          quantity: Number(component.quantity),
        }))
    : [],
  rotation_class: form.rotation_class,
  weight: numberOrNull(form.weight) ?? 0,
  height: numberOrNull(form.height),
  width: numberOrNull(form.width),
  length: numberOrNull(form.length),
  is_active: form.is_active,
});

/**
 * What is still missing before the product can be saved, as the sentence the
 * button shows while it is disabled — or `null` when it is ready to go.
 *
 * @param {object} form
 * @param {number|string|null} companyId - Whose catalog it goes into.
 * @returns {string|null}
 */
export const productProblem = (form, companyId) => {
  if (!companyId) return 'Elige el cliente';
  if (!form.sku.trim()) return 'Falta el SKU';
  if (!form.name.trim()) return 'Falta el nombre';

  if (form.is_kit) {
    if (form.components.length === 0) return 'El kit necesita un componente';
    const isIncomplete = form.components.some(
      (component) => !component.productId || !(Number(component.quantity) > 0)
    );
    if (isIncomplete) return 'Falta el SKU o la cantidad de un componente';
  }

  return null;
};

/** What the unit means for the quantities that will be typed everywhere else. */
export const uomHint = (allowsFractional) =>
  allowsFractional
    ? 'Admite decimales: 2,5 kg es una cantidad válida.'
    : 'Solo cantidades enteras: media unidad se rechaza al recibir o pickear.';

/** "Kit virtual" or "Kit armado". */
export const kitLabel = (product) =>
  `Kit ${product.kit_mode === KIT_MODE.ASSEMBLED ? 'armado' : 'virtual'}`;

/** "POL-001 × 2 · TAZ-010 × 1": what a kit is made of, in one line. */
export const kitSummary = (product, separator = ' · ') =>
  (product.components || [])
    .map((component) => `${component.sku} × ${formatQuantity(component.quantity)}`)
    .join(separator);

const yesNo = (value) => (value ? 'Sí' : 'No');

/**
 * The products on screen as a sheet.
 *
 * @param {Array<object>} products - As `products/catalog/` sent them.
 * @returns {Array<object>}
 */
export const buildCatalogExportRows = (products) =>
  products.map((product) => ({
    Cliente: product.company_name,
    SKU: product.sku,
    Producto: product.name,
    EAN: product.barcode || '',
    Unidad: product.base_uom,
    Fraccionable: yesNo(product.allows_fractional_qty),
    'Controla lote': yesNo(product.lot_controlled),
    'Controla vencimiento': yesNo(product.expiry_controlled),
    'Controla serie': yesNo(product.serial_controlled),
    Kit: product.is_kit ? kitLabel(product) : 'No',
    Componentes: kitSummary(product, ', '),
    Rotación: product.rotation_class,
    'Peso (g)': product.weight,
    Stock: Number(product.stock_total),
    Estado: product.is_active ? 'Activo' : 'De baja',
  }));

/** @param {string|null} clientName - The client filtered by, if any. */
export const catalogExportFileName = (clientName) => `productos-${clientName || 'todos'}.xlsx`;

/** How a product reads in a picker: its SKU first, which is what gets typed. */
export const productOptionLabel = (product) => `${product.sku} — ${product.name}`;
